import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
    Card,
    Table,
    Tag,
    Space,
    Button,
    Switch,
    Row,
    Col,
    Statistic,
    Alert,
    Tooltip,
    Empty,
    Typography,
    Tabs,
    Select,
    Progress,
    Popconfirm,
    Modal,
    DatePicker,
    message,
} from 'antd';
import {
    ReloadOutlined,
    SyncOutlined,
    WarningOutlined,
    CheckCircleOutlined,
    ClockCircleOutlined,
    InboxOutlined,
    ThunderboltOutlined,
    RedoOutlined,
    RollbackOutlined,
    ProfileOutlined,
    CloseCircleOutlined,
} from '@ant-design/icons';
import cdnService from '../../services/cdnService';
import cdnNodeOpsService from '../../services/cdnNodeOpsService';
import dayjs from 'dayjs';

const { Text } = Typography;
const { RangePicker } = DatePicker;

// 各状态展示配置
const STATE_META = {
    pending: { color: 'default', label: '待处理' },
    dispatched: { color: 'processing', label: '已下发' },
    delivered: { color: 'cyan', label: '已送达' },
    acked: { color: 'success', label: '已确认' },
    failed: { color: 'error', label: '失败' },
};

// 任务类型标签
const TASK_TYPE_OPTIONS = [
    { value: 'config_reload', label: 'config_reload' },
    { value: 'cert_update', label: 'cert_update' },
    { value: 'acme_challenge', label: 'acme_challenge' },
    { value: 'cache_purge', label: 'cache_purge' },
    { value: 'cache_prefetch', label: 'cache_prefetch' },
    { value: 'node_command', label: 'node_command' },
    { value: 'ban_ip', label: 'ban_ip' },
    { value: 'unban_ip', label: 'unban_ip' },
];

// lag 毫秒格式化
const fmtLag = (ms) => {
    if (ms === null || ms === undefined) return <Text type="secondary">—</Text>;
    if (ms < 1000) return `${ms} ms`;
    return `${(ms / 1000).toFixed(2)} s`;
};

const POLL_INTERVAL = 10000; // 10s

// ============================================================
// 节点中心（每节点计数 + lag + 失败过滤）
// ============================================================
const NodeCenter = ({ autoRefresh }) => {
    const [nodes, setNodes] = useState([]);
    const [summary, setSummary] = useState(null);
    const [loading, setLoading] = useState(false);
    const [stateFilter, setStateFilter] = useState(''); // '' | pending | dispatched | delivered | acked | failed
    const [sinceHours, setSinceHours] = useState(24); // 时间窗,0=全部;默认 24h 避免历史失败行一直标红
    const [lastUpdated, setLastUpdated] = useState(null);
    const timerRef = useRef(null);

    const loadData = useCallback(async (silent = false) => {
        if (!silent) setLoading(true);
        try {
            const params = { since_hours: sinceHours };
            if (stateFilter) params.state = stateFilter;
            const res = await cdnNodeOpsService.getSyncStatus(params);
            if (res && res.success) {
                const data = res.data || {};
                setNodes(Array.isArray(data.nodes) ? data.nodes : []);
                setSummary(data.summary || null);
                setLastUpdated(new Date());
            } else if (!silent) {
                message.error(res?.error || '加载同步状态失败');
            }
        } catch (e) {
            if (!silent) message.error('加载同步状态失败: ' + e.message);
        } finally {
            if (!silent) setLoading(false);
        }
    }, [stateFilter, sinceHours]);

    useEffect(() => { loadData(); }, [loadData]);

    useEffect(() => {
        if (autoRefresh) {
            timerRef.current = setInterval(() => loadData(true), POLL_INTERVAL);
        }
        return () => {
            if (timerRef.current) { clearInterval(timerRef.current); timerRef.current = null; }
        };
    }, [autoRefresh, loadData]);

    const stateTag = (count, key) => {
        const meta = STATE_META[key] || { color: 'default', label: key };
        if (!count) return <Text type="secondary">0</Text>;
        return <Tag color={meta.color}>{count}</Tag>;
    };

    const columns = [
        {
            title: '节点', dataIndex: 'node_name', key: 'node_name', fixed: 'left',
            render: (name, row) => (
                <Space direction="vertical" size={0}>
                    <Text strong>{name || `#${row.node_id}`}</Text>
                    <Text type="secondary" style={{ fontSize: 12 }}>ID: {row.node_id}</Text>
                </Space>
            ),
        },
        { title: '待处理', dataIndex: 'pending', key: 'pending', align: 'center', sorter: (a, b) => (a.pending || 0) - (b.pending || 0), render: (v) => stateTag(v, 'pending') },
        { title: '已下发', dataIndex: 'dispatched', key: 'dispatched', align: 'center', sorter: (a, b) => (a.dispatched || 0) - (b.dispatched || 0), render: (v) => stateTag(v, 'dispatched') },
        { title: '已送达', dataIndex: 'delivered', key: 'delivered', align: 'center', sorter: (a, b) => (a.delivered || 0) - (b.delivered || 0), render: (v) => stateTag(v, 'delivered') },
        { title: '已确认', dataIndex: 'acked', key: 'acked', align: 'center', sorter: (a, b) => (a.acked || 0) - (b.acked || 0), render: (v) => stateTag(v, 'acked') },
        { title: '失败', dataIndex: 'failed', key: 'failed', align: 'center', sorter: (a, b) => (a.failed || 0) - (b.failed || 0), render: (v) => (v ? <Tag color="error">{v}</Tag> : <Text type="secondary">0</Text>) },
        { title: '总计', dataIndex: 'total', key: 'total', align: 'center', sorter: (a, b) => (a.total || 0) - (b.total || 0), render: (v) => <Text strong>{v || 0}</Text> },
        { title: 'Lag P50', dataIndex: 'lag_p50_ms', key: 'lag_p50_ms', align: 'right', sorter: (a, b) => (a.lag_p50_ms || 0) - (b.lag_p50_ms || 0), render: fmtLag },
        { title: 'Lag P95', dataIndex: 'lag_p95_ms', key: 'lag_p95_ms', align: 'right', sorter: (a, b) => (a.lag_p95_ms || 0) - (b.lag_p95_ms || 0), render: fmtLag },
        {
            title: '落后', dataIndex: 'behind', key: 'behind', align: 'center', sorter: (a, b) => (a.behind || 0) - (b.behind || 0),
            render: (v) => v > 0
                ? <Tag icon={<WarningOutlined />} color="warning">{v}</Tag>
                : <Tag icon={<CheckCircleOutlined />} color="success">0</Tag>,
        },
    ];

    const expandedRowRender = (row) => {
        const recent = Array.isArray(row.recent) ? row.recent : [];
        if (recent.length === 0) return <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="无最近任务" />;
        const recentCols = [
            { title: 'Task ID', dataIndex: 'task_id', key: 'task_id', ellipsis: true },
            { title: '域名ID', dataIndex: 'domain_id', key: 'domain_id', width: 90 },
            { title: '类型', dataIndex: 'task_type', key: 'task_type', width: 120 },
            { title: '状态', dataIndex: 'status', key: 'status', width: 100, render: (s) => { const m = STATE_META[s] || { color: 'default', label: s }; return <Tag color={m.color}>{m.label}</Tag>; } },
            { title: '尝试', dataIndex: 'attempts', key: 'attempts', width: 70, align: 'center' },
            { title: '版本', key: 'version', width: 130, render: (_, r) => <Text style={{ fontSize: 12 }}>{r.applied_version ?? '—'} / {r.target_version ?? '—'}</Text> },
            { title: 'Lag', dataIndex: 'lag_ms', key: 'lag_ms', width: 90, align: 'right', render: fmtLag },
            { title: '错误', dataIndex: 'last_error', key: 'last_error', ellipsis: true, render: (e) => e ? <Tooltip title={e}><Text type="danger" style={{ fontSize: 12 }}>{e}</Text></Tooltip> : <Text type="secondary">—</Text> },
            { title: '创建时间', dataIndex: 'created_at', key: 'created_at', width: 170 },
        ];
        return <Table rowKey={(r) => r.task_id || `${r.domain_id}-${r.created_at}`} columns={recentCols} dataSource={recent} pagination={false} size="small" />;
    };

    const hasAlert = summary && summary.alert;

    return (
        <>
            <Space style={{ marginBottom: 16 }} wrap>
                <Select
                    value={sinceHours}
                    style={{ width: 120 }}
                    onChange={setSinceHours}
                    options={[
                        { value: 1, label: '近 1 小时' },
                        { value: 24, label: '近 24 小时' },
                        { value: 168, label: '近 7 天' },
                        { value: 0, label: '全部' },
                    ]}
                />
                <Select
                    value={stateFilter}
                    style={{ width: 160 }}
                    onChange={setStateFilter}
                    options={[
                        { value: '', label: '全部状态' },
                        { value: 'failed', label: '仅失败' },
                        { value: 'pending', label: '仅待处理' },
                        { value: 'dispatched', label: '仅已下发' },
                        { value: 'delivered', label: '仅已送达' },
                        { value: 'acked', label: '仅已确认' },
                    ]}
                />
                <Button icon={<ReloadOutlined />} onClick={() => loadData()} loading={loading}>刷新</Button>
                {lastUpdated && <Text type="secondary" style={{ fontSize: 12 }}>更新于 {lastUpdated.toLocaleTimeString()}</Text>}
            </Space>

            {hasAlert && (
                <Alert
                    style={{ marginBottom: 16 }} type="warning" showIcon message="检测到同步异常"
                    description={
                        <Space split="·">
                            {summary.total_behind > 0 && <span>共有 {summary.total_behind} 个任务落后</span>}
                            {summary.deadletter_depth > 0 && <span>死信队列深度 {summary.deadletter_depth}</span>}
                        </Space>
                    }
                />
            )}

            <Row gutter={16} style={{ marginBottom: 16 }}>
                <Col xs={12} sm={6}><Card size="small"><Statistic title="上报节点数" value={summary ? summary.nodes_reported : 0} prefix={<ThunderboltOutlined />} /></Card></Col>
                <Col xs={12} sm={6}><Card size="small"><Statistic title="落后任务总数" value={summary ? summary.total_behind : 0} valueStyle={{ color: summary && summary.total_behind > 0 ? '#faad14' : undefined }} prefix={<ClockCircleOutlined />} /></Card></Col>
                <Col xs={12} sm={6}><Card size="small"><Statistic title="死信队列深度" value={summary ? summary.deadletter_depth : 0} valueStyle={{ color: summary && summary.deadletter_depth > 0 ? '#ff4d4f' : undefined }} prefix={<InboxOutlined />} /></Card></Col>
                <Col xs={12} sm={6}><Card size="small"><Statistic title="落后阈值" value={summary ? summary.lag_threshold_s : 0} suffix="秒" /></Card></Col>
            </Row>

            <Table
                rowKey="node_id" columns={columns} dataSource={nodes} loading={loading}
                pagination={false} scroll={{ x: 1100 }} size="middle"
                expandable={{ expandedRowRender }}
                rowClassName={(row) => (row.behind > 0 ? 'cdn-sync-row-behind' : '')}
                locale={{ emptyText: <Empty description="暂无节点同步数据" /> }}
            />
        </>
    );
};

// ============================================================
// 任务中心（task_id 聚合 + 失败节点 + 重推/重置）
// ============================================================
const TaskCenter = ({ autoRefresh }) => {
    const [tasks, setTasks] = useState([]);
    const [summary, setSummary] = useState(null);
    const [loading, setLoading] = useState(false);
    const [typeFilter, setTypeFilter] = useState(undefined);
    const [onlyFailed, setOnlyFailed] = useState(false);
    const [range, setRange] = useState(null);
    const [actionKey, setActionKey] = useState(null); // 正在执行 retry/reset 的 task_id
    const [detail, setDetail] = useState(null); // 详情 modal 数据
    const [detailLoading, setDetailLoading] = useState(false);
    const [lastUpdated, setLastUpdated] = useState(null);
    const timerRef = useRef(null);

    const loadData = useCallback(async (silent = false) => {
        if (!silent) setLoading(true);
        try {
            const params = { limit: 100 };
            if (typeFilter) params.type = typeFilter;
            if (onlyFailed) params.state = 'failed';
            if (range && range[0] && range[1]) {
                params.from = range[0].format('YYYY-MM-DD HH:mm:ss');
                params.to = range[1].format('YYYY-MM-DD HH:mm:ss');
            } else {
                // 未选时间范围:默认只看近 24h
                params.from = dayjs().subtract(24, 'hour').format('YYYY-MM-DD HH:mm:ss');
            }
            const res = await cdnService.getTasksStatus(params);
            if (res && res.success) {
                const data = res.data || {};
                setTasks(Array.isArray(data.tasks) ? data.tasks : []);
                setSummary(data.summary || null);
                setLastUpdated(new Date());
            } else if (!silent) {
                message.error(res?.error || '加载任务列表失败');
            }
        } catch (e) {
            if (!silent) message.error('加载任务列表失败: ' + e.message);
        } finally {
            if (!silent) setLoading(false);
        }
    }, [typeFilter, onlyFailed, range]);

    useEffect(() => { loadData(); }, [loadData]);

    useEffect(() => {
        if (autoRefresh) {
            timerRef.current = setInterval(() => loadData(true), POLL_INTERVAL);
        }
        return () => {
            if (timerRef.current) { clearInterval(timerRef.current); timerRef.current = null; }
        };
    }, [autoRefresh, loadData]);

    const doRetry = async (taskId) => {
        setActionKey(taskId);
        try {
            const res = await cdnService.retryTask(taskId);
            if (res && res.success) {
                message.success(`已重新推送 ${res.data?.reset_to_pending ?? ''} 个节点` + (res.data?.payload_recovered === false ? '（payload 缓存过期，已最小重建）' : ''));
                loadData(true);
            } else {
                message.error(res?.error || '重新推送失败');
            }
        } catch (e) {
            message.error('重新推送失败: ' + e.message);
        } finally {
            setActionKey(null);
        }
    };

    const doReset = async (taskId) => {
        setActionKey(taskId);
        try {
            const res = await cdnService.resetTask(taskId);
            if (res && res.success) {
                message.success(`已重置 ${res.data?.affected ?? 0} 行为待处理`);
                loadData(true);
            } else {
                message.error(res?.error || '重置失败');
            }
        } catch (e) {
            message.error('重置失败: ' + e.message);
        } finally {
            setActionKey(null);
        }
    };

    const openDetail = async (taskId) => {
        setDetailLoading(true);
        setDetail({ task_id: taskId });
        try {
            const res = await cdnService.getTaskDetail(taskId);
            if (res && res.success) {
                setDetail(res.data);
            } else {
                message.error(res?.error || '加载任务详情失败');
                setDetail(null);
            }
        } catch (e) {
            message.error('加载任务详情失败: ' + e.message);
            setDetail(null);
        } finally {
            setDetailLoading(false);
        }
    };

    const columns = [
        {
            title: 'Task ID', dataIndex: 'task_id', key: 'task_id', width: 230, ellipsis: true,
            render: (id) => <Text copyable={{ text: id }} style={{ fontSize: 12 }}>{id}</Text>,
        },
        { title: '类型', dataIndex: 'task_type', key: 'task_type', width: 130, render: (t) => <Tag>{t}</Tag> },
        {
            title: '域名', key: 'domain', width: 160, ellipsis: true,
            render: (_, r) => r.domain
                ? <Tooltip title={r.domain}><Text style={{ fontSize: 12 }}>{r.domain}</Text></Tooltip>
                : (r.domain_id ? <Text type="secondary">#{r.domain_id}</Text> : <Text type="secondary">—</Text>),
        },
        {
            title: '进度', key: 'progress', width: 190,
            render: (_, r) => (
                <Space direction="vertical" size={0} style={{ width: '100%' }}>
                    <Progress
                        percent={r.progress}
                        size="small"
                        status={r.failed > 0 ? 'exception' : (r.progress === 100 ? 'success' : 'active')}
                    />
                    <Text type="secondary" style={{ fontSize: 12 }}>acked {r.acked}/{r.fanout}</Text>
                </Space>
            ),
        },
        {
            title: '状态分布', key: 'states', width: 220,
            render: (_, r) => (
                <Space size={4} wrap>
                    {r.failed > 0 && <Tag color="error">失败 {r.failed}</Tag>}
                    {r.pending > 0 && <Tag>待 {r.pending}</Tag>}
                    {r.dispatched > 0 && <Tag color="processing">发 {r.dispatched}</Tag>}
                    {r.delivered > 0 && <Tag color="cyan">达 {r.delivered}</Tag>}
                    {r.acked > 0 && <Tag color="success">确 {r.acked}</Tag>}
                </Space>
            ),
        },
        { title: '创建时间', dataIndex: 'created_at', key: 'created_at', width: 170 },
        {
            title: '操作', key: 'actions', fixed: 'right', width: 260,
            render: (_, r) => (
                <Space size={4}>
                    <Button size="small" icon={<ProfileOutlined />} onClick={() => openDetail(r.task_id)}>详情</Button>
                    <Popconfirm title="重新推送该任务？" description="失败/待处理节点将重置为待处理并重新入队下发。" onConfirm={() => doRetry(r.task_id)} okText="重新推送" cancelText="取消">
                        <Button size="small" type="primary" ghost icon={<RedoOutlined />} loading={actionKey === r.task_id} disabled={!r.has_failure && r.pending === 0 && r.dispatched === 0 && r.delivered === 0}>重推</Button>
                    </Popconfirm>
                    <Popconfirm title="重置该任务状态？" description="卡住/失败行将重置为待处理（attempts 归零、清错误），不重新下发。" onConfirm={() => doReset(r.task_id)} okText="重置" cancelText="取消">
                        <Button size="small" danger icon={<RollbackOutlined />} loading={actionKey === r.task_id}>重置</Button>
                    </Popconfirm>
                </Space>
            ),
        },
    ];

    // 展开：失败节点明细
    const expandedRowRender = (r) => {
        const failed = Array.isArray(r.failed_nodes) ? r.failed_nodes : [];
        if (failed.length === 0) return <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="该任务无失败节点" />;
        const cols = [
            { title: '节点', key: 'node', width: 200, render: (_, n) => <Space direction="vertical" size={0}><Text strong>{n.node_name || `#${n.node_id}`}</Text><Text type="secondary" style={{ fontSize: 12 }}>ID: {n.node_id}</Text></Space> },
            { title: '尝试', dataIndex: 'attempts', key: 'attempts', width: 70, align: 'center' },
            { title: '失败时间', dataIndex: 'failed_at', key: 'failed_at', width: 170 },
            { title: '错误原因', dataIndex: 'last_error', key: 'last_error', render: (e) => e ? <Text type="danger">{e}</Text> : <Text type="secondary">—</Text> },
        ];
        return (
            <div>
                <Text strong style={{ color: '#ff4d4f' }}><CloseCircleOutlined /> 失败节点 ({failed.length})</Text>
                <Table style={{ marginTop: 8 }} rowKey={(n) => `${r.task_id}-${n.node_id}`} columns={cols} dataSource={failed} pagination={false} size="small" />
            </div>
        );
    };

    // 详情 modal 逐节点表
    const detailCols = [
        { title: '节点', key: 'node', render: (_, n) => <Text strong>{n.node_name || `#${n.node_id}`}</Text> },
        { title: '状态', dataIndex: 'status', key: 'status', width: 100, render: (s) => { const m = STATE_META[s] || { color: 'default', label: s }; return <Tag color={m.color}>{m.label}</Tag>; } },
        { title: '尝试', dataIndex: 'attempts', key: 'attempts', width: 70, align: 'center' },
        { title: '版本', key: 'version', width: 120, render: (_, n) => <Text style={{ fontSize: 12 }}>{n.applied_version ?? '—'} / {n.target_version ?? '—'}</Text> },
        { title: 'Lag', dataIndex: 'lag_ms', key: 'lag_ms', width: 90, align: 'right', render: fmtLag },
        { title: '错误', dataIndex: 'last_error', key: 'last_error', ellipsis: true, render: (e) => e ? <Tooltip title={e}><Text type="danger" style={{ fontSize: 12 }}>{e}</Text></Tooltip> : <Text type="secondary">—</Text> },
        { title: '确认时间', dataIndex: 'acked_at', key: 'acked_at', width: 170 },
    ];

    return (
        <>
            <Space style={{ marginBottom: 16 }} wrap>
                <Select
                    allowClear placeholder="任务类型" value={typeFilter} style={{ width: 180 }}
                    onChange={setTypeFilter} options={TASK_TYPE_OPTIONS}
                />
                <RangePicker showTime value={range} onChange={setRange} placeholder={['默认近 24 小时', '至今']} />
                <Space>
                    <Text type="secondary">仅失败</Text>
                    <Switch checked={onlyFailed} onChange={setOnlyFailed} checkedChildren="开" unCheckedChildren="关" size="small" />
                </Space>
                <Button icon={<ReloadOutlined />} onClick={() => loadData()} loading={loading}>刷新</Button>
                {lastUpdated && <Text type="secondary" style={{ fontSize: 12 }}>更新于 {lastUpdated.toLocaleTimeString()}</Text>}
            </Space>

            <Row gutter={16} style={{ marginBottom: 16 }}>
                <Col xs={12} sm={8}><Card size="small"><Statistic title="返回任务数" value={summary ? summary.tasks_returned : 0} prefix={<ProfileOutlined />} /></Card></Col>
                <Col xs={12} sm={8}><Card size="small"><Statistic title="含失败任务" value={summary ? summary.with_failures : 0} valueStyle={{ color: summary && summary.with_failures > 0 ? '#ff4d4f' : undefined }} prefix={<WarningOutlined />} /></Card></Col>
            </Row>

            <Table
                rowKey="task_id" columns={columns} dataSource={tasks} loading={loading}
                pagination={{ pageSize: 20, showSizeChanger: false }} scroll={{ x: 1300 }} size="middle"
                expandable={{ expandedRowRender, rowExpandable: (r) => r.has_failure }}
                rowClassName={(r) => (r.has_failure ? 'cdn-sync-row-failed' : '')}
                locale={{ emptyText: <Empty description="暂无任务记录" /> }}
            />

            <Modal
                open={!!detail}
                title={<Space><ProfileOutlined /> 任务详情 {detail?.task_id}</Space>}
                onCancel={() => setDetail(null)}
                footer={<Button onClick={() => setDetail(null)}>关闭</Button>}
                width={900}
            >
                {detail && (
                    <>
                        <Space split="·" style={{ marginBottom: 12 }} wrap>
                            <span>类型：<Tag>{detail.task_type}</Tag></span>
                            <span>域名：{detail.domain || (detail.domain_id ? `#${detail.domain_id}` : '—')}</span>
                            <span>扇出：{detail.fanout}</span>
                            <span>进度：{detail.progress}%</span>
                            {detail.has_failure && <Text type="danger">含失败节点</Text>}
                        </Space>
                        <Table
                            rowKey={(n) => n.node_id} columns={detailCols}
                            dataSource={Array.isArray(detail.nodes) ? detail.nodes : []}
                            loading={detailLoading} pagination={false} size="small" scroll={{ x: 800 }}
                            rowClassName={(n) => (n.status === 'failed' ? 'cdn-sync-row-failed' : '')}
                        />
                    </>
                )}
            </Modal>
        </>
    );
};

// ============================================================
// 容器：Tabs + 全局自动刷新开关
// ============================================================
const NodeSyncStatus = () => {
    const [autoRefresh, setAutoRefresh] = useState(false);
    const [activeTab, setActiveTab] = useState('nodes');

    return (
        <Card
            title={<Space><SyncOutlined /><span>节点同步状态 / 任务中心</span></Space>}
            extra={
                <Space>
                    <Text type="secondary" style={{ fontSize: 12 }}>自动刷新(10s)</Text>
                    <Switch checked={autoRefresh} onChange={setAutoRefresh} checkedChildren="开" unCheckedChildren="关" size="small" />
                </Space>
            }
        >
            <Tabs
                activeKey={activeTab}
                onChange={setActiveTab}
                items={[
                    { key: 'nodes', label: <span><ThunderboltOutlined /> 节点中心</span>, children: <NodeCenter autoRefresh={autoRefresh && activeTab === 'nodes'} /> },
                    { key: 'tasks', label: <span><ProfileOutlined /> 任务中心</span>, children: <TaskCenter autoRefresh={autoRefresh && activeTab === 'tasks'} /> },
                ]}
            />

            <style>{`
                .cdn-sync-row-behind > td { background-color: #fffbe6 !important; }
                .cdn-sync-row-failed > td { background-color: #fff1f0 !important; }
            `}</style>
        </Card>
    );
};

export default NodeSyncStatus;
